import { DateTime } from 'luxon';

import { PaycheckFrequency } from 'components/curo-components/forms/IncomeSourcesForm/constants';

export const formatToMonetary = (
  value: number,
  options: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  },
): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...options,
  }).format(value);
};

export const formatToMonth = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'unit',
    unit: 'month',
    unitDisplay: 'long',
  }).format(value);
};

export const formatToPercent = (
  value: number,
  options: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  },
): string => {
  return new Intl.NumberFormat('en-US', { style: 'percent', ...options }).format(value);
};

const getDayWithSuffix = (day: number) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const remainder = day % 10;
  const remainder100 = day % 100;

  if (remainder100 >= 11 && remainder100 <= 13) {
    return day + suffixes[0]; // 11th, 12th, 13th
  }

  switch (remainder) {
    case 1:
      return day + suffixes[1]; // 1st, 21st, 31st
    case 2:
      return day + suffixes[2]; // 2nd, 22nd
    case 3:
      return day + suffixes[3]; // 3rd, 23rd
    default:
      return day + suffixes[0]; // 4th, 5th, etc.
  }
};

const PayFrequencyDays = {
  [PaycheckFrequency.Weekly]: 7,
  [PaycheckFrequency.BiWeekly]: 14,
  [PaycheckFrequency.SemiMonthly]: 15,
  [PaycheckFrequency.Monthly]: 30,
};

export const parseDateToAutoPay = (createdAt: string, payFrequency: PaycheckFrequency, nextPayDate: string) => {
  const bannedStartDays = ['Saturday', 'Sunday'];
  const bannedCalculatedDays = [26, 27, 28, 29, 30, 31];

  const payFrequencyInDays = PayFrequencyDays[payFrequency];

  let currentDate = DateTime.fromISO(createdAt).setZone('America/New_York').setLocale('en-Us');

  const periodStartDate = currentDate.plus({ days: 30 }).startOf('day');
  const periodEndDate = currentDate.plus({ days: 45 }).endOf('day');
  
  let resultDate = DateTime.fromFormat(nextPayDate, 'MM/dd/yyyy')
    .plus({ days: payFrequencyInDays });

  const isValidFirstPaymentDate = (date: DateTime) => {
    return date.hasSame(periodStartDate, 'day') || 
      date.hasSame(periodEndDate, 'day') ||
      (date.toISODate() > periodStartDate.toISODate() && 
       date.toISODate() < periodEndDate.toISODate());
  };

  if (resultDate.toISODate() < periodEndDate.toISODate()) {
    while(!isValidFirstPaymentDate(resultDate)) {
      resultDate = resultDate.plus({ days: payFrequencyInDays });
    }
  } else {
    resultDate = periodStartDate;
  }

  const isValidResultDate = (date: DateTime) =>
    bannedCalculatedDays.every((day) => day !== date.day) && 
    bannedStartDays.every((day) => day !== date.weekdayLong);

  while (!isValidResultDate(resultDate)) {
    resultDate = resultDate.plus({ days: 1 });
  }

  if (resultDate.toISODate() > periodEndDate.toISODate()) {
    resultDate = periodStartDate;

    while (!isValidResultDate(resultDate)) {
      resultDate = resultDate.plus({ days: 1 });
    }
  }

  return {
    day: resultDate.day,
    dayWithSuffix: getDayWithSuffix(resultDate.day),
    month: resultDate.monthLong,
    year: resultDate.year,
  };
};
